/**
 * ElementarySynthBase - Base class for Elementary Audio synthesizers
 *
 * Provides shared functionality for sample-accurate timing:
 * - Fixed voice slots with startSample/endSample
 * - WebRenderer initialization with rendererStartSample tracking
 * - Time-based gate signals using el.time()
 * - Voice slot allocation and cleanup
 */

import { AudioBaseNode, type AudioDependencies } from '@audio/nodes/AudioBaseNode';
import { logger } from '@/logging';
import type { Payload } from '@graph/Payload';
import type { EnvelopeData } from '@nodes/EnvelopeData';
import type { EndpointsRecord } from '@nodes/TypedEndpoints';

// Elementary imports
import { el, NodeRepr_t } from '@elemaudio/core';
import WebRenderer from '@elemaudio/web-renderer';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/** Voice slot - fixed slots are always in the graph, only values change */
export interface VoiceSlot {
  active: boolean;
  note: number;
  velocity: number;
  startSample: number;  // Sample number when gate turns on (sample-accurate note-on)
  endSample: number;    // Sample number when gate turns off (sample-accurate note-off)
}

// ─────────────────────────────────────────────────────────────
// Base Class
// ─────────────────────────────────────────────────────────────

/** Common params that all Elementary synths must have */
interface SynthBaseParams {
  volume: number;
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  defaultPitch: number;
  defaultLength: number;
  defaultVelocity: number;
}

export abstract class ElementarySynthBase<T extends EndpointsRecord> extends AudioBaseNode<T> {
  protected core: WebRenderer | null = null;

  /** Typed access to common synth params (volume, envelope) - unwraps ParamAccessor values */
  protected get synthParams(): SynthBaseParams {
    const p = this.params as any;
    return {
      volume: p.volume?.value ?? -12,
      attack: p.attack?.value ?? 0.01,
      decay: p.decay?.value ?? 0.1,
      sustain: p.sustain?.value ?? 0.5,
      release: p.release?.value ?? 0.2,
      defaultPitch: p.defaultPitch?.value ?? 60,
      defaultLength: p.defaultLength?.value ?? 24,
      defaultVelocity: p.defaultVelocity?.value ?? 0.7
    };
  }
  protected isInitialized = false;

  protected maxVoices = 8;
  protected voiceSlots: VoiceSlot[] = [];

  /** Sample offset between AudioContext and Elementary's el.time() */
  protected rendererStartSample = 0;

  debug = false;

  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
    logger.trace('ElementarySynth', `[CONSTRUCTOR] ${id} created`);
  }

  initialize(): void {
    super.initialize();
    logger.trace('ElementarySynth', `[INITIALIZE] ${this.id}, audioContext: ${!!this.audioContext}`);

    // Initialize fixed voice slots - inactive voices have endSample=0 (in the past)
    this.voiceSlots = Array.from({ length: this.maxVoices }, () => ({
      active: false,
      note: 60,
      velocity: 0,
      startSample: 0,
      endSample: 0
    }));

    this.initializeElementary();
  }

  /**
   * Initialize the Elementary WebRenderer and connect it to audioOut
   */
  protected async initializeElementary(): Promise<void> {
    try {
      this.logTrace(`initializing, audioContext state: ${this.audioContext?.state}`);
      this.core = new WebRenderer();

      const node = await this.core.initialize(this.audioContext as unknown as AudioContext, {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [2]
      });

      // Capture the AudioContext time AFTER renderer initializes - el.time() starts from 0 now
      this.rendererStartSample = Math.floor(this.audioContext.currentTime * this.audioContext.sampleRate);
      this.logTrace(`WebRenderer initialized, rendererStartSample=${this.rendererStartSample}`);

      // Connect Elementary output to audioOut endpoint
      // Use endpointApi directly since T is generic and may not have audioOut in its type
      const gainNode = this.getGainNode('audioOut');
      node.connect(gainNode as unknown as AudioNode);
      this.logTrace(`connected to audioOut`);

      this.isInitialized = true;
      this.render();
      this.logTrace(`initialization complete`);
    } catch (e) {
      this.logError(`Failed to initialize Elementary renderer: ${e}`);
    }
  }

  /**
   * Initialize with an external Elementary renderer (for testing)
   * Accepts WebRenderer or OfflineRenderer
   */
  async initializeRenderer(renderer: WebRenderer | { render: (...args: any[]) => any }): Promise<void> {
    this.core = renderer as WebRenderer;
    this.isInitialized = true;
    this.render();
  }

  dispose(): void {
    logger.trace('ElementarySynth', `[DISPOSE] ${this.id} disposing`);
    this.voiceSlots = [];
    this.core = null;
    this.isInitialized = false;
    super.dispose();
  }

  // ─────────────────────────────────────────────────────────────
  // Event Handling
  // ─────────────────────────────────────────────────────────────

  handleEvent(payload: Payload, _endpointId: string): void {
    // audioContext should be set in AudioBaseNode.initialize()
    // Skip events that arrive before initialization (race condition when adding new instances)
    if (!this.audioContext) {
      logger.trace('ElementarySynth', `[SKIP] ${this.id} event arrived before initialization`);
      return;
    }

    // Use default pitch if no note provided
    const note = this.parseNote(payload.value) ?? this.synthParams.defaultPitch;

    // Duration in ticks, convert to seconds; fallback to defaultLength
    const ticks = payload.duration ?? this.synthParams.defaultLength;
    // ticks -> seconds: (ticks / PPQ) / (BPM / 60)
    const ppq = this.transport.PPQ;
    const bpm = this.transport.bpm.value;
    const durationSec = (ticks / ppq) / (bpm / 60);

    // Use default velocity if not provided
    const velocity = payload.velocity ?? this.synthParams.defaultVelocity;
    const sampleRate = this.audioContext.sampleRate;

    // Calculate sample-accurate start time relative to Elementary's el.time()
    const startTime = payload.time ?? this.audioContext.currentTime;
    const absoluteStartSample = Math.floor(startTime * sampleRate);
    const startSample = absoluteStartSample - this.rendererStartSample;

    // Calculate sample-accurate end time
    const durationSamples = Math.floor(durationSec * sampleRate);
    const endSample = startSample + durationSamples;

    this.logTrace(`handleEvent: note=${note}, ticks=${ticks}, startSample=${startSample}, endSample=${endSample}`);

    this.playNoteWithDuration(note, velocity, startSample, endSample);
  }

  protected parseNote(value: any): number | null {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (Array.isArray(value) && typeof value[0] === 'number') return value[0];
    return null;
  }

  // ─────────────────────────────────────────────────────────────
  // Envelope Handling
  // ─────────────────────────────────────────────────────────────

  protected setFullEnvelope(envelope: EnvelopeData): void {
    if (!envelope) return;
    // Type assertions needed: base class is generic, subclasses define the actual endpoints
    const p = this.params as any;
    if (envelope.attack !== undefined) p.attack?.set(envelope.attack);
    if (envelope.decay !== undefined) p.decay?.set(envelope.decay);
    if (envelope.sustain !== undefined) p.sustain?.set(envelope.sustain);
    if (envelope.release !== undefined) p.release?.set(envelope.release);
    if (this.isInitialized) this.render();
  }

  // ─────────────────────────────────────────────────────────────
  // Voice Slot Management
  // ─────────────────────────────────────────────────────────────

  /**
   * Find an available voice slot, or steal the oldest one.
   */
  protected findVoiceSlot(): number {
    // First, look for an inactive slot
    for (let i = 0; i < this.voiceSlots.length; i++) {
      if (!this.voiceSlots[i].active) {
        return i;
      }
    }
    // All slots active - find the one with earliest endSample (oldest)
    let oldestIdx = 0;
    let oldestEnd = this.voiceSlots[0].endSample;
    for (let i = 1; i < this.voiceSlots.length; i++) {
      if (this.voiceSlots[i].endSample < oldestEnd) {
        oldestEnd = this.voiceSlots[i].endSample;
        oldestIdx = i;
      }
    }
    return oldestIdx;
  }

  /**
   * Play a note with sample-accurate timing.
   */
  protected playNoteWithDuration(note: number, velocity: number, startSample: number, endSample: number): void {
    this.logTrace(`playNoteWithDuration: note=${note}, velocity=${velocity}, startSample=${startSample}, endSample=${endSample}`);

    const slotIdx = this.findVoiceSlot();
    this.voiceSlots[slotIdx] = {
      active: true,
      note,
      velocity,
      startSample,
      endSample
    };

    this.render();

    // Schedule slot deactivation after note ends + release time
    const sampleRate = this.audioContext.sampleRate;
    const absoluteEndSample = endSample + this.rendererStartSample;
    const endTimeSec = absoluteEndSample / sampleRate;
    const releaseMs = this.synthParams.release * 1000;
    const cleanupDelay = Math.max(0, (endTimeSec - this.audioContext.currentTime) * 1000 + releaseMs + 100);

    setTimeout(() => {
      if (this.voiceSlots[slotIdx]?.endSample === endSample) {
        this.voiceSlots[slotIdx].active = false;
      }
    }, cleanupDelay);
  }

  /**
   * Play a note immediately for the given duration.
   */
  playNote(note: number, durationSec: number, velocity = 0.7): void {
    const sampleRate = this.audioContext.sampleRate;
    const absoluteStartSample = Math.floor(this.audioContext.currentTime * sampleRate);
    const startSample = absoluteStartSample - this.rendererStartSample;
    const endSample = startSample + Math.floor(durationSec * sampleRate);
    this.playNoteWithDuration(note, velocity, startSample, endSample);
  }

  // ─────────────────────────────────────────────────────────────
  // DSP Utilities
  // ─────────────────────────────────────────────────────────────

  /**
   * Build a time-based gate signal that is:
   * - 0 while el.time() < startSample
   * - 1 while startSample <= el.time() < endSample
   * - 0 when el.time() >= endSample
   */
  protected buildTimeGate(voiceId: string, startSample: number, endSample: number): NodeRepr_t {
    const time = el.time();
    const start = el.const({ key: `${voiceId}_start`, value: startSample });
    const end = el.const({ key: `${voiceId}_end`, value: endSample });

    const afterStart = el.geq(time, start);
    const beforeEnd = el.le(time, end);
    return el.and(afterStart, beforeEnd);
  }

  /**
   * Build an ADSR envelope with the given gate signal.
   * Uses el.const with keys so parameters can update smoothly without graph rebuild.
   */
  protected buildEnvelope(gate: NodeRepr_t): NodeRepr_t {
    return el.adsr(
      el.const({ key: 'env_attack', value: this.synthParams.attack }),
      el.const({ key: 'env_decay', value: this.synthParams.decay }),
      el.const({ key: 'env_sustain', value: this.synthParams.sustain }),
      el.const({ key: 'env_release', value: this.synthParams.release }),
      gate
    );
  }

  /**
   * Apply volume (dB to linear) and render to core.
   */
  protected applyVolumeAndRender(output: NodeRepr_t): void {
    const volumeDb = this.synthParams.volume;
    const volumeLinear = Math.pow(10, volumeDb / 20);
    const masterGain = el.const({ key: 'master', value: volumeLinear * 0.5 });
    output = el.mul(masterGain, output);

    try {
      this.core!.render(output, output);
    } catch (e) {
      this.logError(`Render error: ${e}`);
    }
  }

  /**
   * Abstract render method - subclasses must implement.
   */
  abstract render(): void;
}
